import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import { makeFetchTransport } from "@sentry/react";
import {
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
} from "react-router-dom";

const getSentryEnvironment = (hostname: string): string => {
    if (hostname === "localhost" || hostname === "127.0.0.1" || hostname.includes("dev")) {
        return "dev";
    }
    return hostname
};

type SentryEnvironment = "qa" | "prod";

const getSentrySamplingEnvironment = (hostname: string): SentryEnvironment => {
    return hostname.includes("qa") ? "qa" : "prod";
};

type SentrySamplingConfig = {
    tracesSampleRate: number;
    replaysSessionSampleRate: number;
    replaysOnErrorSampleRate: number;
};

const SENTRY_SAMPLING_BY_ENV: Record<SentryEnvironment, SentrySamplingConfig> = {
    qa: {
        tracesSampleRate: 0.5,
        replaysSessionSampleRate: 0.2,
        replaysOnErrorSampleRate: 0.5,
    },
    prod: {
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0.05,
        replaysOnErrorSampleRate: 0.5,
    },
};

const getSentryToken = (): string =>
    process.env.REACT_APP_SENTRY_TOKEN ||
    "https://1c2fd355e7126743ed642bb7c113dcf9@o4511052084674560.ingest.us.sentry.io/4511052088606720";

const SENTRY_TUNNEL_PATH = "/v1/timekeeper/v1/log_handler";

const buildSentryTunnelUrl = (): string | null => {
    const gatewayBaseUrl = `${process.env.REACT_APP_TK_GW_DOMAIN || ""}`.replace(/\/+$/, "");
    if (!gatewayBaseUrl) {
        return null;
    }

    return `${gatewayBaseUrl}${SENTRY_TUNNEL_PATH}`;
};

const makeSentryTransport: typeof makeFetchTransport = (options) =>
    makeFetchTransport(options, (url: string, init: RequestInit) => {
        const token = localStorage.getItem("access_token");
        const headers = new Headers(init.headers);
        if (token) {
            headers.set("Authorization", `Bearer ${token}`);
        }
        return fetch(url, { ...init, headers });
    });

const isSentryEnabled = (): boolean => {
    const logEnabled = process.env.LOG_ENABLED ?? process.env.REACT_APP_LOG_ENABLED;
    if (String(logEnabled).toLowerCase() === "true") {
        return true;
    }

    const hostname = window.location.hostname;
    const environment = getSentryEnvironment(hostname);
    return environment !== "dev";
};

const sentryToken = getSentryToken();
if (sentryToken) {
    const hostname = window.location.hostname;
    const environment = getSentryEnvironment(hostname);
    if (isSentryEnabled()) {
        const tunnelUrl = buildSentryTunnelUrl();
        const samplingEnvironment = getSentrySamplingEnvironment(hostname);
        const { tracesSampleRate, replaysSessionSampleRate, replaysOnErrorSampleRate } = SENTRY_SAMPLING_BY_ENV[samplingEnvironment];
        Sentry.init({
            dsn: sentryToken,
            environment: environment,
            tunnel: tunnelUrl || undefined,
            transport: tunnelUrl ? makeSentryTransport : undefined,
            sendDefaultPii: true,
            integrations: [
                Sentry.browserTracingIntegration(),
                Sentry.reactRouterV6BrowserTracingIntegration({
                    useEffect,
                    useLocation,
                    useNavigationType,
                    createRoutesFromChildren,
                    matchRoutes,
                }),
                Sentry.replayIntegration({
                    maskAllText: false,
                    maskAllInputs: false,
                    blockAllMedia: false,
                }),
                //   Sentry.debugIntegration(),
                Sentry.captureConsoleIntegration(),
                Sentry.contextLinesIntegration(),
                Sentry.httpClientIntegration(),
                //   Sentry.sessionTimingIntegration(),
            ],
            // Performance Monitoring
            tracesSampleRate: tracesSampleRate, //  Capture 100% of the transactions
            // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
            tracePropagationTargets: [
                "localhost",
                //     /^https:\/\/yourserver\.io\/api/,
                //     "127.0.0.1:8000",
                "qa-tk-dashboard-wtslus466q-uc.a.run.app",
                //     "ms-fastapi-gateway-164457384651.us-central1.run.app",
                //     "ms-fastapi-auth-164457384651.us-central1.run.app"
            ],

            // Session Replay
            replaysSessionSampleRate: replaysSessionSampleRate,
            replaysOnErrorSampleRate: replaysOnErrorSampleRate,
            enableLogs: true,
        });
        console.log(`Sentry initialized with environment: ${environment}`);
    }
}

